/**
 * Image URL cache with expiration tracking and sessionStorage persistence.
 * Caches signed image URLs to reduce redundant API calls while respecting expiration.
 *
 * Security: Cache is cleared on user sign-out to prevent signed URLs from persisting
 * between user sessions. This prevents unauthorized access to previous user's images.
 *
 * Memory Management: Limited to 200 entries using LRU (Least Recently Used) eviction
 * to prevent excessive memory/storage consumption (~40-100KB in sessionStorage).
 * Cleanup triggered at 80% capacity (160 entries) to maintain performance.
 */

interface CachedImageUrl {
  url: string;
  expiresAt: Date;
  lastAccessed: number;
}

interface StorageLocation {
  bucket: string;
  key: string;
}

const CACHE_KEY_PREFIX = "img-url-cache:";
const MAX_CACHE_SIZE = 200;
const STORAGE_CHECK_THRESHOLD = 160;

class ImageUrlCache {
  private cache = new Map<string, CachedImageUrl>();
  private initialized = false;

  private getCacheKey(location: StorageLocation): string {
    return `${location.bucket}/${location.key}`;
  }

  private getStorageKey(cacheKey: string): string {
    return `${CACHE_KEY_PREFIX}${cacheKey}`;
  }

  private initializeFromStorage(): void {
    if (this.initialized || typeof sessionStorage === "undefined") {
      return;
    }

    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const storageKey = sessionStorage.key(i);
        if (!storageKey?.startsWith(CACHE_KEY_PREFIX)) {
          continue;
        }

        const item = sessionStorage.getItem(storageKey);
        if (!item) {
          continue;
        }

        try {
          const cached = JSON.parse(item) as {
            url: string;
            expiresAt: string;
            lastAccessed: number;
          };
          const expiresAt = new Date(cached.expiresAt);

          if (expiresAt > new Date()) {
            const cacheKey = storageKey.substring(CACHE_KEY_PREFIX.length);
            this.cache.set(cacheKey, {
              url: cached.url,
              expiresAt,
              lastAccessed: cached.lastAccessed,
            });
          } else {
            sessionStorage.removeItem(storageKey);
          }
        } catch {
          sessionStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      console.warn("Failed to initialize image URL cache from storage:", error);
    }

    this.initialized = true;
  }

  private evictLRU(): void {
    if (this.cache.size < MAX_CACHE_SIZE) {
      return;
    }

    let oldestKey: string | null = null;
    let oldestAccess = Number.POSITIVE_INFINITY;

    const entries = Array.from(this.cache.entries());
    for (const [key, value] of entries) {
      if (value.lastAccessed < oldestAccess) {
        oldestAccess = value.lastAccessed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      if (typeof sessionStorage !== "undefined") {
        try {
          sessionStorage.removeItem(this.getStorageKey(oldestKey));
        } catch {
          // Ignore storage errors
        }
      }
    }
  }

  private cleanupExpired(): void {
    const now = new Date();
    const toDelete: string[] = [];

    const entries = Array.from(this.cache.entries());
    for (const [key, value] of entries) {
      if (value.expiresAt <= now) {
        toDelete.push(key);
      }
    }

    for (const key of toDelete) {
      this.cache.delete(key);
      if (typeof sessionStorage !== "undefined") {
        try {
          sessionStorage.removeItem(this.getStorageKey(key));
        } catch {
          // Ignore storage errors
        }
      }
    }
  }

  get(location: StorageLocation): string | null {
    this.initializeFromStorage();

    const cacheKey = this.getCacheKey(location);
    const cached = this.cache.get(cacheKey);

    if (!cached) {
      return null;
    }

    if (cached.expiresAt <= new Date()) {
      this.cache.delete(cacheKey);
      if (typeof sessionStorage !== "undefined") {
        try {
          sessionStorage.removeItem(this.getStorageKey(cacheKey));
        } catch {
          // Ignore storage errors
        }
      }
      return null;
    }

    cached.lastAccessed = Date.now();

    return cached.url;
  }

  set(location: StorageLocation, url: string, expiresAt: Date): void {
    this.initializeFromStorage();

    if (this.cache.size >= STORAGE_CHECK_THRESHOLD) {
      this.cleanupExpired();
      this.evictLRU();
    }

    const cacheKey = this.getCacheKey(location);
    const cached: CachedImageUrl = {
      url,
      expiresAt,
      lastAccessed: Date.now(),
    };

    this.cache.set(cacheKey, cached);

    if (typeof sessionStorage !== "undefined") {
      try {
        sessionStorage.setItem(
          this.getStorageKey(cacheKey),
          JSON.stringify({
            url: cached.url,
            expiresAt: cached.expiresAt.toISOString(),
            lastAccessed: cached.lastAccessed,
          }),
        );
      } catch (error) {
        console.warn("Failed to persist image URL to storage:", error);
        if (error instanceof Error && error.name === "QuotaExceededError") {
          this.evictLRU();
          try {
            sessionStorage.setItem(
              this.getStorageKey(cacheKey),
              JSON.stringify({
                url: cached.url,
                expiresAt: cached.expiresAt.toISOString(),
                lastAccessed: cached.lastAccessed,
              }),
            );
          } catch {
            // If still failing, continue without storage persistence
          }
        }
      }
    }
  }

  clear(): void {
    this.cache.clear();
    if (typeof sessionStorage !== "undefined") {
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key?.startsWith(CACHE_KEY_PREFIX)) {
            keysToRemove.push(key);
          }
        }
        for (const key of keysToRemove) {
          sessionStorage.removeItem(key);
        }
      } catch (error) {
        console.warn("Failed to clear image URL cache from storage:", error);
      }
    }
  }
}

export const imageUrlCache = new ImageUrlCache();
